/**
 * MACONIA 2026 brand tokens — the TypeScript mirror of `tailwind.config.js`.
 *
 * For the places that need a colour in code rather than in a class: chart series,
 * canvas/SVG fills, MUI palette entries, anything computed. Keep this file and the
 * Tailwind config in step — a colour that is right in one and stale in the other shows
 * up in exactly one component and nowhere else.
 *
 * Reference: docs/brand-implementation-guide.md
 */

/** §1 — the five colours the brand actually specifies. Everything else is derived. */
export const BRAND = {
	ink: "#11110F",
	text: "#1B1B1B",
	subtle: "#686761",
	white: "#FFFFFF",
	accent: "#FF5A24",
} as const;

/** §2.2 — the accent needs three forms, not one. */
export const ACCENT = {
	/** CTA hover/pressed fill. 4.39:1 on white — a near miss, still a miss. Never text. */
	dark: "#D9440F",
	/** Active nav pill, selected row, focus fill, chip background. */
	tint: "#FFEDE6",
	/** The only orange you may write with — 5.45:1 on white. */
	strong: "#B8431A",
} as const;

/** §2.3 — warm neutrals. Every step leans yellow, like `subtle` does. */
export const NEUTRAL = {
	900: "#11110F",
	700: "#4A4944",
	600: "#686761",
	500: "#8C8B85",
	400: "#A3A19A",
	350: "#C4C1BA",
	300: "#DCDAD6",
	200: "#E8E6E2",
	100: "#EFEEEB",
	50: "#F4F3F1",
	25: "#FAF9F8",
} as const;

/** §2.4 — warning is amber, not orange: orange belongs to the CTA alone. */
export const SIGNAL = {
	success: "#3DA318",
	warning: "#D9A400",
	danger: "#DF2800",
	successLight: "#DAF8E6",
	warningLight: "#FEEAA3",
	errorLight: "#FFD9DA",
	/** Text on the amber tint — 6.85:1. The amber cannot be written on itself. */
	warningInk: "#7A5300",
} as const;

/**
 * §2.5 — data colours, for charts, categories and severity scales, where colour
 * encodes a value rather than an intent. `high` shares its hex with `ACCENT.strong`
 * by arithmetic; the two must not meet on one screen.
 */
export const SEVERITY = {
	none: "#8C8B85",
	low: "#198754",
	lowMedium: "#A6C13C",
	medium: "#EAB308",
	high: "#B8431A",
	critical: "#DC3545",
} as const;

export const PLUM = "#6B4FA0";

/**
 * §2.6 — avatar fills. Dark enough for white initials, far enough apart to tell two
 * people apart at 24px, and deliberately not the accent.
 */
export const AVATAR_FILLS = [
	"#B8431A",
	"#11110F",
	"#686761",
	"#226B0D",
	"#7A5300",
	"#6B4FA0",
] as const;

/** Ordered series for charts: distinguishable, and clear of the CTA's hue. */
export const CHART_SERIES = [
	"#11110F",
	"#B8431A",
	"#198754",
	"#6B4FA0",
	"#686761",
	"#D9A400",
	"#DC3545",
	"#A6C13C",
] as const;

/** §3 — shape. */
export const RADIUS = {
	button: 6,
	input: 6,
	card: 10,
	hero: 20,
	pill: 9999,
} as const;

/** §3 — elevation. A lift, not a drop. */
export const SHADOW = {
	raised: "6px 6px 18px rgba(0,0,0,0.3)",
	hover: "4px 5px 11px -1px rgba(0,0,0,0.3)",
	none: "none",
} as const;

/** §3 — type. Open Sans first, the bundled Inter behind it, then the system stack. */
export const FONT_STACK =
	'"Open Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
